import { ensureAuthenticated } from '@lune/auth/ensure'
import { ErrorPage } from '@lune/common/errors'
import { printRouterTree } from '@lune/common/router'
import React from 'react'
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom'
import { version } from './api/version'
import { Dashboard } from './components/dashboard'
import { LoginPage as LoginPageComponent } from './pages/login'
import {
  DefaultPage,
  DhcpBlacklistRoute,
  DhcpCategoriesRoute,
  DhcpClassesRoute,
  DhcpGlobalRoute,
  DhcpGroupRoute,
  DhcpMapGroupsRoute,
  DhcpNotesRoute,
  DhcpSubnetRoute,
  DhcpLogRoute,
  DnsPoliciesRoute,
  DnsReverseGroupsRoute,
  DnsSettingsRoute,
  DnsZoneRoute,
  DnsZonesListRoute,
  LegacyFancyLoginPage,
  LegacyLoginFailed,
  LegacyLoginSuccess,
  LoginFailedPage,
  LoginPage,
  LoginSuccessPage,
  Root
} from './paths'

const DhcpCategoriesPage = React.lazy(() =>
  import('./pages/dhcp/categories').then((m) => ({
    default: m.DhcpCategoriesPage
  }))
)
const DhcpClassesPage = React.lazy(() =>
  import('./pages/dhcp/classes').then((m) => ({ default: m.DhcpClassesPage }))
)
const DhcpGlobalPage = React.lazy(() =>
  import('./pages/dhcp/global').then((m) => ({ default: m.DhcpGlobalPage }))
)
const DhcpGroupPage = React.lazy(() =>
  import('./pages/dhcp/group').then((m) => ({ default: m.DhcpGroupPage }))
)
const DhcpMapGroupsPage = React.lazy(() =>
  import('./pages/dhcp/mapGroups').then((m) => ({
    default: m.DhcpMapGroupsPage
  }))
)
const DhcpNotesPage = React.lazy(() =>
  import('./pages/dhcp/notes').then((m) => ({ default: m.DhcpNotesPage }))
)
const DhcpSubnetPage = React.lazy(() =>
  import('./pages/dhcp/subnet').then((m) => ({ default: m.DhcpSubnetPage }))
)
const DnsReverseGroupsPage = React.lazy(() =>
  import('./pages/dns/reverseGroups').then((m) => ({
    default: m.DnsReverseGroupsPage
  }))
)
const DnsSettingsPage = React.lazy(() =>
  import('./pages/dns/settings').then((m) => ({
    default: m.DnsSettingsPage
  }))
)
const DnsZonePage = React.lazy(() =>
  import('./pages/dns/zone').then((m) => ({ default: m.DnsZonePage }))
)
const DnsZonesListPage = React.lazy(() =>
  import('./pages/dns/zones').then((m) => ({ default: m.DnsZonesListPage }))
)
const DhcpLogPage = React.lazy(() =>
  import('./pages/dhcp/log').then((m) => ({ default: m.DhcpLogPage }))
)
const DhcpBlacklistPageComponent = React.lazy(() =>
  import('./pages/dhcp/blacklist').then((m) => ({
    default: m.DhcpBlacklistPage
  }))
)
const DnsPoliciesPageComponent = React.lazy(() =>
  import('./pages/dns/policies').then((m) => ({
    default: m.DnsPoliciesPage
  }))
)

export { AppName } from './constants'

const routerPaths = [
  {
    path: LoginSuccessPage.login.success.router.toString(),
    element: <Navigate to={DefaultPage} replace={true} />,
    errorElement: <ErrorPage />
  },
  {
    path: LoginPage.login.url.toString(),
    element: <LoginPageComponent />,
    errorElement: <ErrorPage />
  },
  {
    path: LegacyFancyLoginPage,
    element: <Navigate to={LoginPage.login.url.toString()} replace={true} />,
    errorElement: <ErrorPage />
  },
  {
    path: LegacyLoginSuccess,
    element: (
      <Navigate
        to={LoginSuccessPage.login.success.url.toString()}
        replace={true}
      />
    ),
    errorElement: <ErrorPage />
  },
  {
    path: LegacyLoginFailed,
    element: (
      <Navigate
        to={LoginFailedPage.login.failed.url.toString()}
        replace={true}
      />
    ),
    errorElement: <ErrorPage />
  },
  {
    path: Root.root.url.toString(),
    element: <Dashboard />,
    loader: () => ensureAuthenticated(version, LoginPage, Root),
    errorElement: <ErrorPage />,
    children: [
      {
        path: Root.root.url.toString(),
        element: <Navigate to={DefaultPage} replace={true} />,
        errorElement: <ErrorPage />
      },
      {
        path: DnsSettingsRoute,
        element: <DnsSettingsPage />,
        errorElement: <ErrorPage />
      },
      {
        path: DnsZonesListRoute,
        element: <DnsZonesListPage />,
        errorElement: <ErrorPage />
      },
      {
        path: DnsZoneRoute,
        element: <DnsZonePage />,
        errorElement: <ErrorPage />
      },
      {
        path: DnsReverseGroupsRoute,
        element: <DnsReverseGroupsPage />,
        errorElement: <ErrorPage />
      },
      {
        path: DnsPoliciesRoute,
        element: <DnsPoliciesPageComponent />,
        errorElement: <ErrorPage />
      },
      {
        path: DhcpGlobalRoute,
        element: <DhcpGlobalPage />,
        errorElement: <ErrorPage />
      },
      {
        path: DhcpMapGroupsRoute,
        element: <DhcpMapGroupsPage />,
        errorElement: <ErrorPage />
      },
      {
        path: DhcpCategoriesRoute,
        element: <DhcpCategoriesPage />,
        errorElement: <ErrorPage />
      },
      {
        path: DhcpClassesRoute,
        element: <DhcpClassesPage />,
        errorElement: <ErrorPage />
      },
      {
        path: DhcpNotesRoute,
        element: <DhcpNotesPage />,
        errorElement: <ErrorPage />
      },
      {
        path: DhcpSubnetRoute,
        element: <DhcpSubnetPage />,
        errorElement: <ErrorPage />
      },
      {
        path: DhcpGroupRoute,
        element: <DhcpGroupPage />,
        errorElement: <ErrorPage />
      },
      {
        path: DhcpLogRoute,
        element: <DhcpLogPage />,
        errorElement: <ErrorPage />
      },
      {
        path: DhcpBlacklistRoute,
        element: <DhcpBlacklistPageComponent />,
        errorElement: <ErrorPage />
      }
    ]
  }
]

printRouterTree(routerPaths)

const router = createBrowserRouter(routerPaths)

export const App: React.FC = () => <RouterProvider router={router} />
